import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { Store, select } from '@ngrx/store';
import { FieldType } from '@ngx-formly/material/form-field';
import {
  DEFAULT_TC_DATA_STATE_KEY,
  getTcData,
  loadTcData,
  NgRxTcDataState,
} from '@tc/data-store';
import { selectByKey } from '@tc/store';
import { hasValue } from '@tc/utils';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs/internal/Subscription';
import { distinctUntilChanged, filter } from 'rxjs/operators';

/**
 * This component is a copy of the FormlyFieldSelect (V) compoennt form formly with all it's functionalities
 * + a dataSource functionality
 * + allow a empty value option ( {value: '', label: 'N/A'} ) functionality
 */
@Component({
  selector: 'tc-formly-smart-select',
  templateUrl: './tc-formly-smart-select.component.html',
})
export class TcFormlySmartSelectComponent extends FieldType implements OnInit {
  /**
   * @ignore
   * Base formly functionality from version v5.6.1 that got removed in later verions.
   * Needs to be declared in order to allow an empty value (option: {value: '', label: 'N/A'}).
   * Otherwise the input label collapses ofer the option label.
   */
  @ViewChild(MatSelect, { static: true }) formFieldControl!: MatSelect;

  /**
   * @ignore
   * Base formly functionality
   * The default options of the component passed through FieldType
   */
  defaultOptions = {
    templateOptions: {
      options: [],
      compareWith(o1: any, o2: any) {
        return o1 === o2;
      },
      /**
       * default value as the mat-select doesn't support lazy load and until it is developed
       * we must load everything. I chose 1000 because I have to specify it otherwise it will default to
       * the default page size (200). 1000 options in a list is already too much but for now it will have to do.
       * Also any number bigger than this will get reduced to 1000 as we have a limit set in the backend.
       */
      take: 1000,
    },
  };

  /**
   * @ignore
   * Base formly functionality
   */
  private selectAllValue!: { options: any; value: any[] };

  /**
   * @ignore
   * Base formly functionality
   */
  getSelectAllState(options: any[]) {
    if (this.empty || this.value.length === 0) {
      return null;
    }

    return this.value.length !== this.getSelectAllValue(options).length
      ? 'indeterminate'
      : 'checked';
  }

  /**
   * @ignore
   * Base formly functionality
   */
  toggleSelectAll(options: any[]) {
    const selectAllValue = this.getSelectAllValue(options);
    this.formControl.setValue(
      !this.value || this.value.length !== selectAllValue.length
        ? selectAllValue
        : []
    );
    this.formControl.markAsDirty();
  }

  /**
   * @ignore
   * Base formly functionality
   * Calls the change function specified by the user in case there is one.
   * @param $event
   */
  change($event: MatSelectChange) {
    this.to.change?.(this.field, $event);
  }

  /**
   * @ignore
   * Base formly functionality
   */
  _getAriaLabelledby() {
    if (this.to.attributes?.['aria-labelledby']) {
      return this.to.attributes['aria-labelledby'] as string;
    }

    return this.formField?._labelId;
  }

  /**
   * @ignore
   * Base formly functionality
   */
  _getAriaLabel() {
    return this.to.attributes?.['aria-label'] as string;
  }

  /**
   * @ignore
   * Base formly functionality
   */
  private getSelectAllValue(options: any[]) {
    if (!this.selectAllValue || options !== this.selectAllValue.options) {
      const flatOptions: any[] = [];
      options.forEach((o) =>
        o.group ? flatOptions.push(...o.group) : flatOptions.push(o)
      );

      this.selectAllValue = {
        options,
        value: flatOptions.filter((o) => !o.disabled).map((o) => o.value),
      };
    }

    return this.selectAllValue.value;
  }

  //  **** Custom login starts here ****

  /**
   * Observable of the data store
   */
  private dataStore$: Observable<NgRxTcDataState>;

  /**
   * Field used as option label
   */
  public labelFieldName: string;

  /**
   * The items from the data store
   */
  public items: any[];

  /**
   * Subscription to the data store for items
   */
  private itemsSubscription: Subscription;

  /**
   * @ignore
   */
  constructor(private readonly store$: Store<any>) {
    super();
    this.dataStore$ = this.store$.pipe(
      select(DEFAULT_TC_DATA_STATE_KEY),
      filter(hasValue),
      distinctUntilChanged()
    );
  }

  /**
   * @ignore
   */
  ngOnInit(): void {
    super.ngOnInit();

    // If we have a data provider we need to load the data into the store and
    // subscribe to it. Otherwise this component acts like a regular FormlySelect component.
    if (this.to.dataProvider) {
      const {
        storeKey,
        dataProvider: { fields: defaultLabelFieldName },
        labelFieldName,
      } = this.to;

      this.labelFieldName = labelFieldName || defaultLabelFieldName;

      this.loadData();

      this.itemsSubscription = selectByKey(
        getTcData,
        this.dataStore$,
        storeKey
      ).subscribe((options) => {
        this.items = options;
      });
    }
  }

  /**
   * @ignore
   */
  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.itemsSubscription?.unsubscribe();
  }

  /**
   * Dispatches the loadTcData action thus making a request
   * to the server for the options and loading the into the store.
   */
  loadData() {
    this.store$.dispatch(
      loadTcData({
        storeKey: this.to.storeKey,
        skip: 0,
        take: this.to.take,
      })
    );
  }

  /**
   * Returns what is emited by the select when the an option is selected
   * @param item i.e. selected option
   * @returns Default value is the whole option. But if the multiselect
   * the distinct property in the dataSource that it return just the value of the distinct option
   */
  itemValue(item) {
    if (this.to.dataProvider.distinct) {
      return item[this.labelFieldName];
    }

    return item;
  }
}
